import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Modal } from 'react-bootstrap'
import { Circles } from 'react-loader-spinner'
import noop from 'lodash/noop'

// Credit card icon (trans-staging's getSvgIcon('Credit_card'))
const getSvgIcon = (name, color) => (
    <svg xmlns="http://www.w3.org/2000/svg" data-name="Layer 1" viewBox="0 0 64 80" x="0px" y="0px" fill={color}>
        <path d="M50,15H14c-3.309,0-6,2.691-6,6v22c0,3.309,2.691,6,6,6H50c3.309,0,6-2.691,6-6V21c0-3.309-2.691-6-6-6ZM14,19H50c1.103,0,2,.897,2,2v1.6H12v-1.6c0-1.103,.897-2,2-2Zm36,26H14c-1.103,0-2-.897-2-2v-14H52v14c0,1.103-.897,2-2,2Z" />
        <path d="M29,38h-12c-1.104,0-2,.896-2,2s.896,2,2,2h12c1.104,0,2-.896,2-2s-.896-2-2-2Z" />
        <path d="M17,36h6c1.104,0,2-.896,2-2s-.896-2-2-2h-6c-1.104,0-2,.896-2,2s.896,2,2,2Z" />
        <circle cx="45" cy="37" r="4" />
    </svg>
)

// Title row of the header: full width without the w-100 class (some portals make .w-100 display: block !important)
const HEADER_ROW_STYLE = { display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'nowrap' }

function PaymentMethodModal({
    show,
    onHide,
    isPaymentLocked,
    isSubmitting,
    paymentProcessing,
    walletButtonShow,
    showApplePayButton,
    showGooglePayButton,
    isIOSDevice,
    showTnc,
    termsAccepted,
    setTermsAccepted,
    cardContent,
    savedCardContent,
    currentSelCard,
    walletContent,
    amount,
    paybyCardorWallet,
    onTabChange,
    tab
}) {
    const showWalletOption = walletButtonShow && (
        isIOSDevice
            ? showApplePayButton == '1'
            : showGooglePayButton == '1'
    )
    const hasSavedCard = !!savedCardContent

    const [view, setView] = useState('selector')
    const [innerTab, setActiveTabState] = useState('card')
    // The screen may drive the tab (e.g. the Saved Card tab once a new card is saved); otherwise the modal keeps it
    const activeTab = tab || innerTab
    // The screen hears every method change (e.g. to show the new card form or use the saved card)
    const setActiveTab = (method) => {
        setActiveTabState(method)
        if (onTabChange) onTabChange(method)
    }

    // A tab chosen by the screen (not one the modal already set) opens the details view on that tab
    useEffect(() => {
        if (show && tab && tab !== innerTab) {
            setActiveTabState(tab)
            setView('details')
        }
    }, [tab])
    const [highlightTnc, setHighlightTnc] = useState(false)

    // Methods offered (same rules as the selector); with only one the details view opens on it directly
    const methods = [
        paybyCardorWallet && 'card',
        hasSavedCard && paybyCardorWallet && 'saved-card',
        showWalletOption && 'wallet'
    ].filter(Boolean)
    const singleMethod = methods.length === 1 ? methods[0] : null

    useEffect(() => {
        if (show) {
            setView((!singleMethod && (showWalletOption || hasSavedCard)) ? 'selector' : 'details')
            setActiveTab(singleMethod || 'card')
            setTermsAccepted(false)
        }
    }, [show])

    useEffect(() => {
        if (!show) return
        const handleContextMenu = (e) => e.preventDefault()
        const handleKeyDown = (e) => {
            if (
                e.key === 'F12' ||
                (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(e.key)) ||
                (e.ctrlKey && e.key === 'U')
            ) {
                e.preventDefault()
            }
        }
        document.addEventListener('contextmenu', handleContextMenu)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('contextmenu', handleContextMenu)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [show])

    const handleMethodSelect = (method) => {
        setActiveTab(method)
        setTermsAccepted(false)
        setView('details')
    }

    const handleTabChange = (tab) => {
        if (isPaymentLocked) return
        setActiveTab(tab)
        setTermsAccepted(false)
    }

    const handleWalletOverlayClick = () => {
        setHighlightTnc(true)
        setTimeout(() => setHighlightTnc(false), 2000)
    }

    const renderSpinner = () => (
        <div className="text-center py-5">
            <Circles
                height="52" width="52"
                color="var(--bs-primary)"
                ariaLabel="circles-loading"
                visible={true}
                wrapperStyle={{ justifyContent: 'center' }}
            />
            <p className="fw-semibold mt-3 mb-1">Processing your payment…</p>
            <p className="text-muted small mb-0">Please do not close this window.</p>
        </div>
    )

    const renderSelectorView = () => (
        <>
            <Modal.Header className="border-0 pb-0 pt-3 px-4">
                <div className="d-flex align-items-center justify-content-between" style={HEADER_ROW_STYLE}>
                    <h5 className="mb-0 fw-bold flex-grow-1 text-center" style={{ flex: '1 1 auto', minWidth: 0 }}>Payment Details</h5>
                    <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none text-secondary"
                        style={{ lineHeight: 1, flexShrink: 0 }}
                        onClick={onHide}
                        disabled={isPaymentLocked}
                        aria-label="Close"
                    >
                        <i className="fa fa-times fa-lg" />
                    </button>
                </div>
            </Modal.Header>

            <Modal.Body className="px-4 py-3">
                <div className="d-flex align-items-center gap-2 mb-4 justify-content-center">
                    <i className="fa fa-lock text-muted small" />
                    <span className="text-muted small">Your payment information is secure and encrypted</span>
                </div>

                <h5 className="fw-bold mb-1">How would you like to pay?</h5>
                <p className="text-muted small mb-4">Choose a payment method to continue.</p>

                {/* New Card row */}
                {paybyCardorWallet ? <div
                    className="d-flex align-items-center p-3 border rounded-3 mb-3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleMethodSelect('card')}
                >
                    <span style={{
                        width: 46, height: 46, borderRadius: 10,
                        background: 'rgba(var(--bs-primary-rgb), 0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                        <span style={{ width: 22, height: 22 }}>
                            {getSvgIcon('Credit_card', 'var(--bs-primary)')}
                        </span>
                    </span>
                    <div className="ms-3 flex-grow-1">
                        <div className="fw-bold">New Card</div>
                        <div className="text-muted small">Pay using a new card</div>
                    </div>
                    <i className="fa fa-chevron-right text-muted" />
                </div> : null}

                {/* Saved Card row */}
                {hasSavedCard && paybyCardorWallet && (
                    <div
                        className="d-flex align-items-center p-3 border rounded-3 mb-3"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleMethodSelect('saved-card')}
                    >
                        <span style={{
                            width: 46, height: 46, borderRadius: 10,
                            background: 'rgba(59, 91, 219, 0.12)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                            <span style={{ width: 22, height: 22 }}>
                                {getSvgIcon('Credit_card', '#3b5bdb')}
                            </span>
                        </span>
                        <div className="ms-3 flex-grow-1">
                            <div className="fw-bold">Saved Card</div>
                            <div className="text-muted small">Pay using your saved card</div>
                            {currentSelCard?.card_last_four && (
                                <div className="d-flex align-items-center gap-1 mt-1">
                                    <span className="small text-muted">
                                        {currentSelCard?.card_type ? currentSelCard.card_type : 'Card'} ••••{currentSelCard.card_last_four}
                                    </span>
                                </div>
                            )}
                        </div>
                        <i className="fa fa-chevron-right text-muted" />
                    </div>
                )}

                {/* Apple Pay / Google Pay row */}
                {showWalletOption && (
                    <div
                        className="d-flex align-items-center p-3 border rounded-3 mb-3"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleMethodSelect('wallet')}
                    >
                        <span style={{
                            width: 46, height: 46, borderRadius: 10,
                            background: 'rgba(25, 135, 84, 0.12)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            fontSize: '10px', fontWeight: 700, lineHeight: 1.2, textAlign: 'center',
                            color: '#198754'
                        }}>
                            <span>G Pay<br />&#63743; Pay</span>
                        </span>
                        <div className="ms-3 flex-grow-1">
                            <div className="fw-bold">Apple Pay / Google Pay</div>
                            <div className="text-muted small">Pay securely using your wallet</div>
                        </div>
                        <i className="fa fa-chevron-right text-muted" />
                    </div>
                )}

                <div className="text-center mt-4 pb-2">
                    <div className="fw-semibold small mb-1">
                        <i className="fa fa-shield text-muted me-1" />
                        100% Secure Payments
                    </div>
                    <div className="text-muted small">Powered by trusted payment partners</div>
                </div>
            </Modal.Body>
        </>
    )

    // One method only: no tabs and no way back to the selector
    const showTabBar = !singleMethod && (showWalletOption || hasSavedCard)

    const renderDetailsView = () => (
        <>
            <Modal.Header className="border-0 pb-0 pt-3 px-4">
                <div className="d-flex align-items-center" style={HEADER_ROW_STYLE}>
                    {showTabBar && (
                        <button
                            type="button"
                            className="btn btn-link p-0 text-decoration-none text-secondary me-2"
                            style={{ lineHeight: 1, flexShrink: 0 }}
                            onClick={() => { if (!isPaymentLocked) setView('selector') }}
                            disabled={isPaymentLocked}
                            aria-label="Back"
                        >
                            <i className="fa fa-arrow-left" />
                        </button>
                    )}
                    <h5 className="mb-0 fw-bold flex-grow-1 text-center" style={{ flex: '1 1 auto', minWidth: 0 }}>Payment Details</h5>
                    <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none text-secondary"
                        style={{ lineHeight: 1, flexShrink: 0 }}
                        onClick={onHide}
                        disabled={isPaymentLocked}
                        aria-label="Close"
                    >
                        <i className="fa fa-times fa-lg" />
                    </button>
                </div>
            </Modal.Header>

            {showTabBar && (
                <div className="d-flex px-3 pt-1 gap-1 border-bottom">
                    {/* New Card tab */}
                    {paybyCardorWallet ? <button
                        type="button"
                        className={`btn btn-link px-3 pb-2 fw-semibold text-decoration-none border-0 rounded-0 d-flex align-items-center gap-1 ${activeTab === 'card' ? 'text-primary' : 'text-muted'
                            }`}
                        style={{ borderBottom: `2px solid ${activeTab === 'card' ? 'var(--bs-primary)' : 'transparent'}`, marginBottom: '-1px', fontSize: '1rem' }}
                        onClick={() => handleTabChange('card')}
                        disabled={isPaymentLocked}
                    >
                        <span style={{ width: 20, height: 20, alignItems: 'center' }}>
                            {getSvgIcon('Credit_card', activeTab === 'card' ? 'var(--bs-primary)' : '#6c757d')}
                        </span>
                        New Card
                    </button> : null}

                    {/* Saved Card tab */}
                    {hasSavedCard && paybyCardorWallet && (
                        <button
                            type="button"
                            className={`btn btn-link px-3 pb-2 fw-semibold text-decoration-none border-0 rounded-0 d-flex align-items-center gap-1 ${activeTab === 'saved-card' ? 'text-primary' : 'text-muted'
                                }`}
                            style={{ borderBottom: `2px solid ${activeTab === 'saved-card' ? 'var(--bs-primary)' : 'transparent'}`, marginBottom: '-1px', fontSize: '1rem' }}
                            onClick={() => handleTabChange('saved-card')}
                            disabled={isPaymentLocked}
                        >
                            <span style={{ width: 20, height: 20, alignItems: 'center' }}>
                                {getSvgIcon('Credit_card', activeTab === 'saved-card' ? 'var(--bs-primary)' : '#6c757d')}
                            </span>
                            Saved Card
                        </button>
                    )}

                    {/* Apple Pay / GPay tab */}
                    {showWalletOption && (
                        <button
                            type="button"
                            className={`btn btn-link px-3 pb-2 fw-semibold text-decoration-none border-0 rounded-0 d-flex align-items-center gap-1 ${activeTab === 'wallet' ? 'text-primary' : 'text-muted'
                                }`}
                            style={{ borderBottom: `2px solid ${activeTab === 'wallet' ? 'var(--bs-primary)' : 'transparent'}`, marginBottom: '-1px', fontSize: '1rem' }}
                            onClick={() => handleTabChange('wallet')}
                            disabled={isPaymentLocked}
                        >
                            Apple Pay / GPay
                        </button>
                    )}
                </div>
            )}

            <Modal.Body className="px-2 px-sm-3 py-3">
                {/* New Card tab body */}
                {activeTab === 'card' && (
                    (paymentProcessing || isSubmitting) ? renderSpinner() : cardContent
                )}

                {/* Saved Card tab body */}
                {activeTab === 'saved-card' && (
                    (paymentProcessing || isSubmitting) ? renderSpinner() : savedCardContent
                )}

                {/* Wallet tab body */}
                {activeTab === 'wallet' && (
                    (paymentProcessing || isSubmitting) ? renderSpinner() : (
                        <div>
                            <div className="text-center pt-2 pb-4">
                                <div className="d-flex justify-content-center gap-3 mb-3">
                                    <span style={{
                                        width: 56, height: 56, borderRadius: 12,
                                        background: '#f0f2f5',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        <i className="fa fa-credit-card text-secondary" style={{ fontSize: 22 }} />
                                    </span>
                                    <span style={{
                                        width: 56, height: 56, borderRadius: 12,
                                        background: '#f0f2f5',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        <i className="fa fa-mobile text-secondary" style={{ fontSize: 28 }} />
                                    </span>
                                </div>
                                <div className="fw-bold mb-1">Secure checkout with<br />Apple Pay / Google Pay</div>
                                <div className="text-muted small">You will be redirected to complete<br />your payment securely.</div>
                            </div>

                            {amount && (
                                <div className="d-flex justify-content-between align-items-center py-2 border-top border-bottom mb-3">
                                    <span className="fw-semibold">Amount Due</span>
                                    <span className="fw-bold">${Number(amount).toFixed(2)}</span>
                                </div>
                            )}

                            {showTnc && (
                                <div className="mb-3 d-flex align-items-start gap-2">
                                    <input
                                        type="checkbox"
                                        checked={termsAccepted || false}
                                        onChange={(e) => setTermsAccepted(e.target.checked)}
                                        style={{
                                            minWidth: 16, width: 16, height: 16,
                                            marginTop: 2, flexShrink: 0, cursor: 'pointer',
                                            outline: highlightTnc ? '2px solid #dc3545' : undefined
                                        }}
                                    />
                                    <span className="small">By continuing, you agree to the terms and conditions</span>
                                </div>
                            )}
                            <div style={{ position: 'relative' }}>
                                {walletContent}
                                {showTnc && !termsAccepted && (
                                    <div
                                        style={{ position: 'absolute', inset: 0, zIndex: 5, cursor: 'not-allowed' }}
                                        onClick={handleWalletOverlayClick}
                                    />
                                )}
                            </div>
                        </div>
                    )
                )}
            </Modal.Body>
        </>
    )

    return (
        <Modal
            show={show}
            onHide={isPaymentLocked ? noop : onHide}
            backdrop="static"
            keyboard={false}
            centered
            dialogClassName="tfpm-dialog"
            contentClassName="tfpm-content"
        >
            <style>{`
                .tfpm-dialog .tfpm-content {
                    border-radius: 8px !important;
                    overflow: hidden;
                }
                /* A gateway's card form (Stripe / Datacap) inside the modal: no second card frame and title */
                .tfpm-content .pm-gateway .card {
                    border: 0 !important;
                    box-shadow: none !important;
                    background: transparent;
                }
                .tfpm-content .pm-gateway .card-header {
                    display: none;
                }
                .tfpm-content .pm-gateway .card-body {
                    padding: 0;
                }
                /* Its Submit full width, as the Pay button of the other tabs */
                .tfpm-content .pm-gateway #q-box__buttons {
                    margin-top: 1rem !important;
                }
                .tfpm-content .pm-gateway #q-box__buttons > .btn {
                    width: 100%;
                    justify-content: center;
                }
            `}</style>
            {view === 'selector' ? renderSelectorView() : renderDetailsView()}
        </Modal>
    )
}

PaymentMethodModal.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func,
    isPaymentLocked: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    paymentProcessing: PropTypes.bool,
    walletButtonShow: PropTypes.bool,
    showApplePayButton: PropTypes.string,
    showGooglePayButton: PropTypes.string,
    isIOSDevice: PropTypes.bool,
    showTnc: PropTypes.bool,
    termsAccepted: PropTypes.bool,
    setTermsAccepted: PropTypes.func,
    cardContent: PropTypes.node,
    savedCardContent: PropTypes.node,
    currentSelCard: PropTypes.object,
    walletContent: PropTypes.node,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paybyCardorWallet: PropTypes.bool,
    // Called with 'card' | 'saved-card' | 'wallet' when the modal opens and when the method changes
    onTabChange: PropTypes.func,
    // Optional: the tab the screen wants shown ('card' | 'saved-card' | 'wallet'); keep it in step through onTabChange
    tab: PropTypes.string,
}

PaymentMethodModal.defaultProps = {
    show: false,
    onHide: noop,
    isPaymentLocked: false,
    isSubmitting: false,
    paymentProcessing: false,
    walletButtonShow: false,
    showApplePayButton: '0',
    showGooglePayButton: '0',
    isIOSDevice: false,
    showTnc: false,
    termsAccepted: false,
    setTermsAccepted: noop,
    cardContent: null,
    savedCardContent: null,
    currentSelCard: null,
    walletContent: null,
    amount: null,
    paybyCardorWallet: false,
    onTabChange: null,
    tab: null
}

export default PaymentMethodModal
